/**
 * Routine の締めの確定(routine_closings)を Gate で見るための決まり(2026-10-23・表示だけ)。
 *  - 段: ①請求(元請けごと)②下請けの支払い(会社ごと)③ドライバーの支払い(人ごと)④燃料
 *  - Gate での状態: 確定から作った取引(transaction_ids)と請求書をたどって出す
 *      ① → 売上の取引が請求書に入っているか(未発行 / 発行済み 番号)
 *      ②③ → 支払の取引の状態(承認待ち / 支払い待ち / 支払い済み / 完了)。いちばん進んでいないものを出す
 *  - ①の作業内訳: 日付 × ドライバーの表(Routine の作業内訳 PDF と同じ並び)
 *
 * 具体例は tests/unit/routineClosing.test.ts
 */
import type { DateStr, Invoice, MonthStr, RoutineBillingDetail, RoutineClosing, RoutineClosingStage, Transaction, TransactionStatus, Yen } from '@core/lib/types';

export const STAGE_LABELS: Record<RoutineClosingStage, string> = {
  billing: '①請求(元請け)',
  subcontract: '②下請けの支払い',
  driver: '③ドライバーの支払い',
  fuel: '④燃料',
};

export const STAGE_ORDER: RoutineClosingStage[] = ['billing', 'subcontract', 'driver', 'fuel'];

export type GateState = {
  label: string;
  tone: 'ok' | 'plan' | 'pend' | 'warn' | 'none';
  /** 開く画面(Gate の URL)と、リンクの文字 */
  to: string | null;
  linkLabel: string | null;
};

const PAYMENT_STEPS: Array<{ statuses: TransactionStatus[]; state: Omit<GateState, 'to' | 'linkLabel'>; to: string | null; linkLabel: string | null }> = [
  { statuses: ['rejected', 'cancelled'], state: { label: '差戻し・取消', tone: 'warn' }, to: null, linkLabel: null },
  { statuses: ['applied', 'manager_checked'], state: { label: '承認待ち', tone: 'pend' }, to: '/approve', linkLabel: '承認へ' },
  { statuses: ['approved', 'cash_ready', 'received'], state: { label: '支払い待ち', tone: 'plan' }, to: '/pay', linkLabel: '支払い待ちへ' },
  { statuses: ['paid'], state: { label: '支払い済み', tone: 'ok' }, to: null, linkLabel: null },
  { statuses: ['completed'], state: { label: '完了', tone: 'ok' }, to: null, linkLabel: null },
];

/** 確定が Gate のどこまで進んだか */
export function gateStateOf(
  c: Pick<RoutineClosing, 'stage' | 'status' | 'transaction_ids'>,
  txById: Map<string, Pick<Transaction, 'id' | 'status'>>,
  invoiceByTx: Map<string, Pick<Invoice, 'id' | 'number' | 'status'>>,
): GateState {
  const none = (label: string, tone: GateState['tone'] = 'none'): GateState => ({ label, tone, to: null, linkLabel: null });
  if (c.status === 'cancelled') return none('取り消し(Routine)', 'warn');
  if (c.stage === 'fuel') return none('—');
  if (c.transaction_ids.length === 0) return none('Gate の記録なし', 'warn');
  const txs = c.transaction_ids.map((id) => txById.get(id));
  if (txs.some((t) => !t)) return none('Gate の記録が見つからない', 'warn');

  if (c.stage === 'billing') {
    const invoices = new Map<string, Pick<Invoice, 'id' | 'number' | 'status'>>();
    let billed = 0;
    for (const id of c.transaction_ids) {
      const inv = invoiceByTx.get(id);
      if (inv && inv.status !== 'void') {
        billed++;
        invoices.set(inv.id, inv);
      }
    }
    if (billed === 0) return { label: '請求書 未発行', tone: 'pend', to: '/invoices', linkLabel: '請求書へ' };
    const numbers = [...invoices.values()].map((i) => i.number).join('・');
    if (billed < c.transaction_ids.length) return { label: `一部 請求済み(${numbers})`, tone: 'warn', to: '/invoices', linkLabel: '請求書へ' };
    return { label: `請求書 ${numbers}`, tone: 'ok', to: '/invoices', linkLabel: '請求書へ' };
  }

  // ②③: いちばん進んでいない取引の状態
  for (const step of PAYMENT_STEPS) {
    if (txs.some((t) => t && step.statuses.includes(t.status))) return { ...step.state, to: step.to, linkLabel: step.linkLabel };
  }
  return none('—');
}

/** 段ごとの件数・合計(取り消し済みは数えない) */
export function summarizeClosings(closings: RoutineClosing[]): Record<RoutineClosingStage, { count: number; total: Yen }> {
  const out = Object.fromEntries(STAGE_ORDER.map((s) => [s, { count: 0, total: 0 }])) as Record<RoutineClosingStage, { count: number; total: Yen }>;
  for (const c of closings) {
    if (c.status === 'cancelled') continue;
    out[c.stage].count++;
    out[c.stage].total += c.total;
  }
  return out;
}

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

/** その月の日付(1日〜末日)と曜日 */
export function monthDays(month: MonthStr): Array<{ date: DateStr; day: number; weekday: string; sunday: boolean }> {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const n = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return Array.from({ length: n }, (_, i) => {
    const date = `${month}-${String(i + 1).padStart(2, '0')}`;
    const w = new Date(`${date}T00:00:00Z`).getUTCDay();
    return { date, day: i + 1, weekday: WEEKDAYS[w]!, sunday: w === 0 };
  });
}

/** ①の作業内訳の合計(拠点の合計 + ガソリン折半 + 表の下の行) */
export function billingTotal(d: RoutineBillingDetail): Yen {
  return (
    d.locations.reduce((a, l) => a + l.sum, 0) + d.gas.reduce((a, g) => a + g.half, 0) + d.extras.reduce((a, x) => a + x.amount, 0)
  );
}

/** ①の稼働人数(列の数) */
export function billingColumnCount(d: RoutineBillingDetail): number {
  return d.locations.reduce((a, l) => a + l.columns.length, 0);
}

/** 'YYYY-MM' → '2026年9月' */
export function monthLabel(month: MonthStr): string {
  return `${month.slice(0, 4)}年${Number(month.slice(5, 7))}月`;
}

/** ISO の日時 → '10/02 11:00'(JST) */
export function shortDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const j = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(j.getUTCMonth() + 1)}/${p(j.getUTCDate())} ${p(j.getUTCHours())}:${p(j.getUTCMinutes())}`;
}

/** 請求書の明細(transaction_id)→ 請求書 */
export function invoiceByTransaction(invoices: Array<Pick<Invoice, 'id' | 'number' | 'status' | 'lines'>>): Map<string, Pick<Invoice, 'id' | 'number' | 'status'>> {
  const map = new Map<string, Pick<Invoice, 'id' | 'number' | 'status'>>();
  for (const inv of invoices) {
    if (inv.status === 'void') continue;
    for (const l of inv.lines) if (l.transaction_id) map.set(l.transaction_id, inv);
  }
  return map;
}
